import React, { useContext } from 'react';
import { ActiveDatabaseContext } from '../../../../../Components/ActiveDatabaseContext/ActiveDatabaseContext';
import { IStartedRoutine } from './startedRoutineQueries';
import EDTypography from '../../../../../BaseComponents/Typography/EDTypography';
import Logger, { namedError } from '../../../../../Utils/Logger';
import {
  rutinKeys,
  rutinInfos,
} from '../../../../Delsystem/_Common/Const/RutinInfo';
import {
  findRutinOrStyrvärde,
  findStyrvärde,
} from '../../../../Delsystem/_Common/Utils/RutinUtils';
import { rutinRouteName, styrvardeRouteName } from '../../../../Routing/router';
import EDList from '../../../../../BaseComponents/List/EDList';
import EDLinkListItem from '../../../../../BaseComponents/List/LinkList/EDLinkListItem';
import { Leveransstatus } from '../../../../../Types/RutinInfoType';
import { makeStyles } from 'tss-react/mui';
import DeliveryStatusTag from '../../../../../Components/DeliveryStatusTag/DeliveryStatusTag';
import { isEmpty } from 'ramda';
import {
  currentRoutingVersion,
  encodeBaseParamValue,
  useBaseRouteValue,
} from '../../../../Routing/Utils/routerUtils';
import EDSkeletonList from '../../../../../BaseComponents/Skeleton/EDSkeletonList';

interface IProps {
  /** Defaults to subtitle1 */
  itemsTypographyVariant?: React.ComponentProps<typeof EDTypography>['variant'];
  hideLeveransstatus?: boolean;
}

interface ILocalStartedRoutine {
  database: string;
  routine: string;
  path: string;
  leveransstatus: Leveransstatus;
}

export const STARTED_ROUTINES_TITLE = 'Senast startade rutiner';
export const STARTED_ROUTINES_ICON_TYPE = 'bookmark';

const getRoutine = (
  routine: IStartedRoutine,
  subSystemPath: string
): ILocalStartedRoutine | null => {
  const key = rutinKeys[routine.routine];
  if (key) {
    if (findRutinOrStyrvärde(key)) {
      const rutinInfo = rutinInfos[key];
      const styrvärde = findStyrvärde(key) != undefined;
      const rutinEllerStyrvärdeRoute = styrvärde
        ? styrvardeRouteName
        : rutinRouteName;
      if (rutinInfo) {
        if (rutinInfo.rutinID === routine.routine) {
          return {
            database: routine.dbname,
            routine: rutinInfo.benamning,
            path: `${subSystemPath}/${rutinEllerStyrvärdeRoute}/${rutinInfo.rutinURL}`,
            leveransstatus: rutinInfo.leveransstatus,
          };
        }
      } else {
        Logger.error(
          namedError(
            'StartedRoutinesMissingRutinInfo',
            `${key} saknas i rutinInfo`
          )
        );
      }
    } else {
      Logger.warn(
        namedError(
          'StartedRoutinesMissingRutin',
          `${key} saknas i rutiner eller styrvärden`
        )
      );
    }
  } else {
    Logger.warn(
      namedError(
        'StartedRoutinesMissingRutinKey',
        `${routine.routine} saknas i rutinKeys`
      )
    );
  }

  return null;
};

const useStyles = makeStyles()(
  theme =>
    ({
      root: {
        flexDirection: 'row-reverse',
        paddingLeft: theme.spacing(7.25),
      },
    } as const)
);

const StartedRoutines: React.FunctionComponent<
  React.PropsWithChildren<IProps>
> = props => {
  const { classes } = useStyles();
  const activeDatabaseContext = useContext(ActiveDatabaseContext);
  const { systemPath, usernamePath } = useBaseRouteValue();
  const startedroutines =
    activeDatabaseContext.activeDatabaseInfo?.me?.at(0)?.startedroutines ?? [];
  const availableRoutines = activeDatabaseContext.availableRoutines ?? [];
  if (
    !activeDatabaseContext.activeDatabaseInfoLoading &&
    isEmpty(startedroutines) &&
    activeDatabaseContext.activeDatabaseInfoError
  ) {
    return (
      <EDTypography color="error">
        Fel vid hämtning av databasinformation:{' '}
        {activeDatabaseContext.activeDatabaseInfoError.message}
      </EDTypography>
    );
  }
  if (activeDatabaseContext.activeDatabaseInfoLoading) {
    return <EDSkeletonList rows={5} rowHeight={45} gap={12} />;
  }
  if (isEmpty(startedroutines)) {
    return <EDTypography>Du har inte startat några rutiner än</EDTypography>;
  }

  let startedRoutinesLinks: ILocalStartedRoutine[] = [];
  startedroutines
    .filter(r => availableRoutines[r.routine])
    .forEach(x => {
      const routine = getRoutine(
        x,
        `/${currentRoutingVersion}/${systemPath}/${usernamePath}/${encodeBaseParamValue(
          x.dbname,
          'none'
        )}`
      );
      if (routine) {
        startedRoutinesLinks.push(routine);
      }
    });
  return (
    <EDList component="nav" style={{ flex: 1 }} testId="startedRoutinesList">
      {startedRoutinesLinks.map((x: ILocalStartedRoutine, index: number) => {
        const showLeveransstatus =
          x.leveransstatus !== 'Active' && !props.hideLeveransstatus;
        return (
          <EDLinkListItem
            key={x.path + index}
            to={x.path}
            text={x.routine}
            typographyProps={{
              variant: props.itemsTypographyVariant || 'subtitle1',
            }}
            icon={
              showLeveransstatus ? (
                <DeliveryStatusTag leveransstatus={x.leveransstatus} />
              ) : undefined
            }
            classes={{
              listItem: classes.root,
            }}
            testId={x.routine}
          />
        );
      })}
    </EDList>
  );
};

export default StartedRoutines;
